import { keepPreviousData, type QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { isAxiosError } from 'axios'
import { buildTimeline, type TimelineEntry } from '@/domain/work-order-timeline'
import { notification } from '@/notifications/notification-store'
import { activityApi } from '@/services/activity.service'
import { type CommentDTO, type CommentInput, commentApi } from '@/services/comment.service'

const commentsKey = (id: string) => ['work-order-comments', id]

/** Root of the work-order-activity key family. Exported so other hooks and the realtime query bridge invalidate the same key. */
export const WORK_ORDER_ACTIVITY_KEY = 'work-order-activity'

const activityKey = (id: string) => [WORK_ORDER_ACTIVITY_KEY, id]

/**
 * How long to wait before re-checking the audit log once after a mutation.
 *
 * The audit row is not written by the request the user just made: the service
 * publishes to EventBridge and a separate consumer persists it off SQS, so the
 * mutation's response returns BEFORE the row exists. Invalidating only at the
 * moment of the mutation refetches a log that does not yet contain the very
 * event the user caused, so there is a second pass a moment later.
 *
 * Two requests, not a poll. A bounded poll converges regardless of how slow the
 * bus is, which one delayed pass cannot, but it costs a request every couple of
 * seconds for as long as the window runs - too much traffic for the benefit on a
 * surface a reviewer opens occasionally. The accepted consequence: when the
 * consumer is slower than this delay (a cold Lambda will be), the entry appears
 * on the next refocus, reopen, mutation or reload rather than on its own.
 */
const AUDIT_PROPAGATION_MS = 4000

/**
 * Refresh a work order's audit log after a mutation that records an event.
 *
 * Pass an `id` to narrow to one order, or omit it when the caller does not know
 * which order was affected. Both passes are cheap: react-query refetches only
 * queries with a live observer, which is the order actually on screen.
 */
export const invalidateWorkOrderActivity = (qc: QueryClient, id?: string): void => {
	const queryKey = id ? activityKey(id) : [WORK_ORDER_ACTIVITY_KEY]
	void qc.invalidateQueries({ queryKey })
	setTimeout(() => void qc.invalidateQueries({ queryKey }), AUDIT_PROPAGATION_MS)
}

/**
 * Comments for a work order. The endpoint is admin-only (ADR-0012); a non-admin
 * JWT gets 403. We let the query surface that error (no retry) and the merge
 * hook derives `commentsForbidden` from it — the activity feed still renders and
 * the composer is hidden, so a 403 degrades gracefully instead of blocking.
 */
export const useWorkOrderComments = (id: string | null) =>
	useQuery<CommentDTO[]>({
		queryKey: commentsKey(id ?? ''),
		queryFn: () => commentApi.listForWorkOrder(id as string),
		enabled: !!id,
		retry: false,
	})

/**
 * One page of the work order's audit log (creations, edits, transitions, blocks,
 * uploads, validation and image requests, checklist completions), newest first.
 *
 * The page number is part of the query key so paging back is served from cache
 * rather than refetched, mirroring `useWebhooks`. `WORK_ORDER_ACTIVITY_KEY`
 * stays the family root, so the realtime bridge's prefix invalidation still
 * matches every page.
 */
export const useWorkOrderActivity = (id: string | null, page = 1) =>
	useQuery({
		queryKey: [...activityKey(id ?? ''), page],
		queryFn: () => activityApi.listForWorkOrder(id as string, { page }),
		enabled: !!id,
		// Hold the previous page while the next one loads. Without it the pager
		// briefly reads `pagination` as undefined, and any consumer clamping its
		// page against `totalPages` would bounce straight back to page 1.
		placeholderData: keepPreviousData,
	})

/**
 * The comment half of the D10 timeline — the whole thread, oldest first.
 *
 * Activity is deliberately NOT merged in here: it is paged and newest-first,
 * comments are unpaginated and oldest-first, and sorting the two into one array
 * would destroy the ordering each side needs. The Activity tab reads
 * {@link useWorkOrderActivity} directly.
 *
 * `commentsForbidden` lets the UI hide the composer when the caller can't post.
 */
export function useWorkOrderTimeline(id: string | null): {
	entries: TimelineEntry[]
	isLoading: boolean
	commentsForbidden: boolean
} {
	const comments = useWorkOrderComments(id)
	const commentsForbidden = isAxiosError(comments.error) && comments.error.response?.status === 403
	return {
		entries: buildTimeline(comments.data ?? []),
		isLoading: comments.isLoading,
		commentsForbidden,
	}
}

/**
 * A refused write is the likeliest failure on this thread, and an unreported one
 * looks exactly like a dead button. Both mutations say what happened.
 */
const failed = (what: string) => (error: unknown) =>
	notification.error(
		isAxiosError(error) && error.response?.status === 403
			? `You are not allowed to ${what}.`
			: `Could not ${what}. Try again.`,
	)

/** Post a new comment, then refresh the thread. */
export const useCreateComment = (id: string) => {
	const qc = useQueryClient()
	return useMutation({
		mutationFn: (input: CommentInput) => commentApi.create(id, input),
		onSuccess: () => {
			void qc.invalidateQueries({ queryKey: commentsKey(id) })
			invalidateWorkOrderActivity(qc, id)
		},
		onError: failed('post this comment'),
	})
}

/** Delete a comment (soft-delete), then refresh the thread. */
export const useDeleteComment = (id: string) => {
	const qc = useQueryClient()
	return useMutation({
		mutationFn: (commentId: string) => commentApi.remove(commentId),
		onSuccess: () => {
			void qc.invalidateQueries({ queryKey: commentsKey(id) })
			invalidateWorkOrderActivity(qc, id)
		},
		onError: failed('delete this comment'),
	})
}
